import React from "react";
import {
  FaUserGraduate,
  FaChalkboardTeacher,
  FaBookOpen,
  FaAward,
  FaArrowRight,
  FaCheckCircle,
  FaStar,
  FaGraduationCap,
} from "react-icons/fa";

import girlStudent from "./images/smiling.jpg";
import happyStudents from "./images/smiling.jpg";
import libraryStudent from "./smiling.jpg";
import teacherStudent from "./images/smiling.jpg";

/* =========================================================
   PURPLE & GOLD — PROFESSIONAL SCHOOL DESIGN
   All styling is included in this component.
========================================================= */

const cardStyles = `
.mmi-premium-section {
  --mmi-purple: #4366ff;
  --mmi-purple-dark: #450cef;
  --mmi-purple-deep: #0f013e;
  --mmi-purple-soft: #f1eaff;
  --mmi-gold: #facc15;
  --mmi-gold-dark: #eab308;
  --mmi-ink: #241535;
  --mmi-muted: #716780;
  --mmi-border: #eae3f5;

  position: relative;
  isolation: isolate;
  width: 100%;
  padding: clamp(60px, 7vw, 100px) clamp(16px, 4vw, 48px);
  overflow: hidden;
  background:
    radial-gradient(ellipse at 0% 0%, rgba(109, 40, 217, .08), transparent 34%),
    radial-gradient(ellipse at 100% 100%, rgba(250, 204, 21, .10), transparent 30%),
    #fcfbff;
  color: var(--mmi-ink);
  font-family: "Poppins", "Inter", system-ui, sans-serif;
  box-sizing: border-box;
}

.mmi-premium-section *,
.mmi-premium-section *::before,
.mmi-premium-section *::after {
  box-sizing: border-box;
}

.mmi-premium-section a {
  text-decoration: none;
}

.mmi-premium-section button,
.mmi-premium-section a {
  -webkit-tap-highlight-color: transparent;
}

.mmi-premium-section button:focus-visible,
.mmi-premium-section a:focus-visible {
  outline: 3px solid var(--mmi-gold-dark);
  outline-offset: 4px;
}

/* Decorative background details */

.mmi-premium-decoration {
  position: absolute;
  z-index: -1;
  width: 280px;
  height: 280px;
  border: 1px solid rgba(109, 40, 217, .08);
  border-radius: 50%;
  pointer-events: none;
}

.mmi-premium-decoration.top {
  top: -155px;
  right: -100px;
  box-shadow:
    0 0 0 28px rgba(109, 40, 217, .025),
    0 0 0 58px rgba(109, 40, 217, .02);
}

.mmi-premium-decoration.bottom {
  bottom: -190px;
  left: -110px;
  width: 350px;
  height: 350px;
  border-color: rgba(234, 179, 8, .12);
}

/* =========================================================
   SECTION INTRODUCTION
========================================================= */

.mmi-premium-header {
  width: 100%;
  max-width: 850px;
  margin: 0 auto 53px;
  text-align: center;
}

.mmi-premium-eyebrow {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-bottom: 21px;
  padding: 10px 17px;
  border: 1px solid rgba(109, 40, 217, .15);
  border-radius: 100px;
  background: #ffffff;
  box-shadow: 0 5px 18px rgba(59, 7, 100, .045);
  color: var(--mmi-purple);
  font-size: 10px;
  font-weight: 850;
  letter-spacing: 1.8px;
  text-transform: uppercase;
}

.mmi-premium-eyebrow svg {
  color: var(--mmi-gold-dark);
  font-size: 16px;
}

.mmi-premium-heading {
  margin: 0;
  color: var(--mmi-purple-dark);
  font-size: clamp(30px, 4.3vw, 49px);
  font-weight: 900;
  letter-spacing: -1.8px;
  line-height: 1.2;
  text-wrap: balance;
}

.mmi-premium-heading-accent {
  display: block;
  margin-top: 10px;
  color: var(--mmi-purple);
  font-size: clamp(19px, 2.5vw, 27px);
  font-weight: 700;
  letter-spacing: -.65px;
  line-height: 1.5;
}

.mmi-premium-heading-rule {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  margin: 23px auto 20px;
}

.mmi-premium-heading-rule span {
  display: block;
  height: 4px;
  border-radius: 99px;
}

.mmi-premium-heading-rule span:first-child {
  width: 36px;
  background: var(--mmi-purple);
}

.mmi-premium-heading-rule span:nth-child(2) {
  width: 70px;
  background: var(--mmi-gold);
}

.mmi-premium-heading-rule span:last-child {
  width: 36px;
  background: var(--mmi-purple);
}

.mmi-premium-description {
  max-width: 690px;
  margin: 0 auto;
  color: var(--mmi-muted);
  font-size: 14px;
  font-weight: 400;
  line-height: 2;
}

/* Trust points below the introduction */

.mmi-premium-trust-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 12px 26px;
  margin-top: 24px;
}

.mmi-premium-trust-item {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #59466e;
  font-size: 11px;
  font-weight: 700;
}

.mmi-premium-trust-item svg {
  color: var(--mmi-purple);
  font-size: 14px;
}

.mmi-premium-trust-item.gold svg {
  color: var(--mmi-gold-dark);
}

/* =========================================================
   FEATURE CARD GRID
========================================================= */

.mmi-premium-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 22px;
  width: 100%;
  max-width: 1480px;
  margin: 0 auto;
  align-items: stretch;
}

/* =========================================================
   FEATURE CARD
========================================================= */

.mmi-premium-card {
  position: relative;
  display: flex;
  min-width: 0;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid rgba(91, 45, 133, .11);
  border-radius: 22px;
  background: #ffffff;
  box-shadow:
    0 8px 22px rgba(48, 24, 77, .045),
    0 1px 2px rgba(48, 24, 77, .025);
  transition:
    transform 300ms ease,
    box-shadow 300ms ease,
    border-color 300ms ease;
}

.mmi-premium-card::before {
  position: absolute;
  z-index: 3;
  top: 0;
  left: 0;
  width: 0;
  height: 4px;
  content: "";
  background: linear-gradient(90deg, var(--mmi-purple), var(--mmi-gold));
  transition: width 350ms ease;
}

.mmi-premium-card:hover {
  transform: translateY(-9px);
  border-color: rgba(109, 40, 217, .25);
  box-shadow:
    0 22px 48px rgba(59, 7, 100, .12),
    0 5px 15px rgba(59, 7, 100, .04);
}

.mmi-premium-card:hover::before {
  width: 100%;
}

/* Image */

.mmi-premium-image-wrap {
  position: relative;
  height: 235px;
  flex-shrink: 0;
  overflow: hidden;
  background: linear-gradient(135deg, #ede9fe, #d8c7f7);
}

.mmi-premium-image {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  transition: transform 650ms cubic-bezier(.2, .7, .2, 1);
}

.mmi-premium-card:hover .mmi-premium-image {
  transform: scale(1.075);
}

.mmi-premium-image-overlay {
  position: absolute;
  inset: 0;
  background:
    linear-gradient(
      180deg,
      rgba(37, 11, 62, .04) 0%,
      rgba(37, 11, 62, .08) 38%,
      rgba(37, 11, 62, .80) 100%
    ),
    linear-gradient(
      125deg,
      rgba(109, 40, 217, .13),
      transparent 65%
    );
  pointer-events: none;
}

/* Number badge */

.mmi-premium-number {
  position: absolute;
  top: 15px;
  left: 15px;
  display: grid;
  width: 36px;
  height: 36px;
  place-items: center;
  border: 1px solid rgba(255, 255, 255, .38);
  border-radius: 11px;
  background: rgba(43, 14, 70, .65);
  color: #ffffff;
  font-size: 11px;
  font-weight: 850;
  letter-spacing: .5px;
  backdrop-filter: blur(10px);
}

/* Image label */

.mmi-premium-image-label {
  position: absolute;
  right: 13px;
  bottom: 15px;
  left: 73px;
  color: #ffffff;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 1.25px;
  line-height: 1.5;
  text-align: right;
  text-transform: uppercase;
}

/* Icon overlapping image and content */

.mmi-premium-icon {
  position: absolute;
  z-index: 2;
  bottom: -23px;
  left: 21px;
  display: grid;
  width: 54px;
  height: 54px;
  place-items: center;
  border: 4px solid #ffffff;
  border-radius: 17px;
  background: var(--mmi-purple);
  color: #ffffff;
  font-size: 21px;
  box-shadow: 0 6px 17px rgba(59, 7, 100, .18);
  transition:
    transform 250ms ease,
    background 250ms ease,
    color 250ms ease;
}

.mmi-premium-card:hover .mmi-premium-icon {
  transform: translateY(-3px) rotate(-4deg);
  background: var(--mmi-gold);
  color: var(--mmi-purple-dark);
}

/* =========================================================
   CARD CONTENT
========================================================= */

.mmi-premium-card-content {
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: flex-start;
  padding: 38px 21px 22px;
}

.mmi-premium-card-category {
  display: inline-block;
  margin-bottom: 10px;
  color: var(--mmi-purple);
  font-size: 9px;
  font-weight: 900;
  letter-spacing: 1.35px;
  text-transform: uppercase;
}

.mmi-premium-card-title {
  margin: 0 0 12px;
  color: var(--mmi-purple-dark);
  font-size: 18px;
  font-weight: 850;
  letter-spacing: -.35px;
  line-height: 1.45;
  overflow-wrap: anywhere;
}

.mmi-premium-card-description {
  flex: 1;
  margin: 0;
  color: var(--mmi-muted);
  font-size: 12px;
  font-weight: 400;
  line-height: 1.95;
}

.mmi-premium-card-footer {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-top: 24px;
  padding-top: 17px;
  border-top: 1px solid #f0ebf7;
}

.mmi-premium-card-link {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--mmi-purple);
  font-size: 11px;
  font-weight: 850;
  cursor: pointer;
  transition: color 200ms ease, gap 200ms ease;
}

.mmi-premium-card-link svg {
  font-size: 12px;
  transition: transform 200ms ease;
}

.mmi-premium-card-link:hover {
  gap: 13px;
  color: var(--mmi-purple-dark);
}

.mmi-premium-card-link:hover svg {
  transform: translateX(3px);
}

.mmi-premium-card-star {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #9a6b00;
  font-size: 10px;
}

.mmi-premium-card-star svg {
  color: var(--mmi-gold-dark);
  font-size: 12px;
}

/* =========================================================
   BOTTOM STATEMENT
========================================================= */

.mmi-premium-bottom {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  width: 100%;
  max-width: 1480px;
  margin: 38px auto 0;
  padding: 25px 30px;
  overflow: hidden;
  border: 1px solid rgba(250, 204, 21, .22);
  border-radius: 19px;
  background: linear-gradient(115deg, #32104f, #54208b);
  box-shadow: 0 13px 30px rgba(59, 7, 100, .12);
  color: #ffffff;
}

.mmi-premium-bottom::after {
  position: absolute;
  top: -75px;
  right: 12%;
  width: 180px;
  height: 180px;
  content: "";
  border: 1px solid rgba(250, 204, 21, .20);
  border-radius: 50%;
  box-shadow: 0 0 0 22px rgba(250, 204, 21, .035);
  pointer-events: none;
}

.mmi-premium-bottom-copy {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;
}

.mmi-premium-bottom-icon {
  display: grid;
  width: 49px;
  height: 49px;
  flex-shrink: 0;
  place-items: center;
  border-radius: 15px;
  background: var(--mmi-gold);
  color: var(--mmi-purple-dark);
  font-size: 21px;
}

.mmi-premium-bottom-title {
  display: block;
  margin: 0 0 5px;
  color: #ffffff;
  font-size: 15px;
  font-weight: 850;
  line-height: 1.5;
}

.mmi-premium-bottom-description {
  display: block;
  color: #e6d9f7;
  font-size: 11px;
  line-height: 1.8;
}

.mmi-premium-bottom-badge {
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex-shrink: 0;
  padding: 12px 17px;
  border: 1px solid rgba(250, 204, 21, .38);
  border-radius: 11px;
  background: rgba(250, 204, 21, .10);
  color: #fde68a;
  font-size: 10px;
  font-weight: 850;
  letter-spacing: .7px;
}

.mmi-premium-bottom-badge svg {
  color: var(--mmi-gold);
}

/* =========================================================
   RESPONSIVE: LAPTOP
========================================================= */

@media (max-width: 1200px) {
  .mmi-premium-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 22px;
    max-width: 900px;
  }

  .mmi-premium-image-wrap {
    height: 255px;
  }

  .mmi-premium-card-content {
    padding-right: 24px;
    padding-left: 24px;
  }

  .mmi-premium-card-title {
    font-size: 19px;
  }

  .mmi-premium-card-description {
    font-size: 13px;
  }
}

/* =========================================================
   RESPONSIVE: TABLET AND MOBILE
========================================================= */

@media (max-width: 650px) {
  .mmi-premium-section {
    padding: 60px 16px;
  }

  .mmi-premium-header {
    margin-bottom: 35px;
  }

  .mmi-premium-eyebrow {
    margin-bottom: 17px;
    padding: 9px 13px;
    font-size: 9px;
    letter-spacing: 1.2px;
  }

  .mmi-premium-heading {
    font-size: clamp(28px, 7vw, 38px);
    letter-spacing: -1px;
  }

  .mmi-premium-heading-accent {
    margin-top: 8px;
    font-size: clamp(18px, 4.7vw, 23px);
    letter-spacing: -.4px;
  }

  .mmi-premium-heading-rule {
    margin: 19px auto 16px;
  }

  .mmi-premium-description {
    font-size: 13px;
    line-height: 1.9;
  }

  .mmi-premium-trust-row {
    gap: 12px 16px;
    margin-top: 21px;
  }

  .mmi-premium-trust-item {
    font-size: 10px;
  }

  .mmi-premium-grid {
    grid-template-columns: minmax(0, 1fr);
    gap: 19px;
    max-width: 440px;
  }

  .mmi-premium-card {
    border-radius: 20px;
  }

  .mmi-premium-image-wrap {
    height: 245px;
  }

  .mmi-premium-card-content {
    padding: 39px 21px 21px;
  }

  .mmi-premium-card-title {
    font-size: 19px;
  }

  .mmi-premium-card-description {
    font-size: 12px;
  }

  .mmi-premium-bottom {
    flex-direction: column;
    align-items: flex-start;
    gap: 19px;
    margin-top: 25px;
    padding: 21px;
  }

  .mmi-premium-bottom-title {
    font-size: 14px;
  }

  .mmi-premium-bottom-badge {
    align-self: flex-start;
  }
}

@media (max-width: 360px) {
  .mmi-premium-section {
    padding-right: 12px;
    padding-left: 12px;
  }

  .mmi-premium-image-wrap {
    height: 210px;
  }

  .mmi-premium-card-content {
    padding-right: 17px;
    padding-left: 17px;
  }

  .mmi-premium-bottom-copy {
    align-items: flex-start;
    gap: 11px;
  }

  .mmi-premium-bottom-icon {
    width: 42px;
    height: 42px;
  }
}

/* =========================================================
   ACCESSIBILITY
========================================================= */

@media (prefers-reduced-motion: reduce) {
  .mmi-premium-section *,
  .mmi-premium-section *::before,
  .mmi-premium-section *::after {
    animation-duration: .01ms !important;
    animation-iteration-count: 1 !important;
    scroll-behavior: auto !important;
    transition-duration: .01ms !important;
  }
}
`;

const featureCards = [
  {
    id: 1,
    image: girlStudent,
    icon: <FaUserGraduate />,
    number: "01",
    category: "Our Commitment",
    imageLabel: "Every Student Matters",
    title: "Quality Education",
    description:
      "We provide a well-rounded education that nurtures academic excellence, character, leadership, and lifelong learning in every student.",
  },
  {
    id: 2,
    image: happyStudents,
    icon: <FaBookOpen />,
    number: "02",
    category: "Our Approach",
    imageLabel: "Learning Without Limits",
    title: "Modern Learning",
    description:
      "Our engaging classrooms, innovative teaching methods, and digital resources inspire creativity, curiosity, and confidence.",
  },
  {
    id: 3,
    image: libraryStudent,
    icon: <FaChalkboardTeacher />,
    number: "03",
    category: "Our Educators",
    imageLabel: "Guidance That Inspires",
    title: "Experienced Teachers",
    description:
      "Our qualified and passionate educators are committed to guiding students toward academic success and personal growth.",
  },
  {
    id: 4,
    image: teacherStudent,
    icon: <FaAward />,
    number: "04",
    category: "Our Values",
    imageLabel: "Character. Purpose. Success.",
    title: "Excellence & Integrity",
    description:
      "We cultivate discipline, integrity, leadership, and excellence while preparing students to become responsible global citizens.",
  },
];

const Card = () => {
  return (
    <>
      <style>{cardStyles}</style>

      <section
        className="mmi-premium-section"
        aria-labelledby="mmi-premium-heading"
      >
        {/* Decorative elements */}

        <div
          className="mmi-premium-decoration top"
          aria-hidden="true"
        />

        <div
          className="mmi-premium-decoration bottom"
          aria-hidden="true"
        />

        {/* ===============================================
            SECTION INTRODUCTION
        =============================================== */}

        <header className="mmi-premium-header">
          <div className="mmi-premium-eyebrow">
            <FaGraduationCap />
            <span>Discover the Difference</span>
          </div>

          <h2
            id="mmi-premium-heading"
            className="mmi-premium-heading"
          >
            We Build Future Leaders

            <span className="mmi-premium-heading-accent">
              Through Quality, Values-Driven Education
            </span>
          </h2>

          <div
            className="mmi-premium-heading-rule"
            aria-hidden="true"
          >
            <span />
            <span />
            <span />
          </div>

          <p className="mmi-premium-description">
            We create a supportive learning environment where every
            student can discover their potential, develop strong
            character, achieve academic success, and build the
            confidence to shape a brighter future.
          </p>

          <div className="mmi-premium-trust-row">
            <span className="mmi-premium-trust-item">
              <FaCheckCircle />
              Academic Growth
            </span>

            <span className="mmi-premium-trust-item gold">
              <FaCheckCircle />
              Character Development
            </span>

            <span className="mmi-premium-trust-item">
              <FaCheckCircle />
              Future Readiness
            </span>
          </div>
        </header>

        {/* ===============================================
            FEATURE CARDS
        =============================================== */}

        <div className="mmi-premium-grid">
          {featureCards.map((item) => (
            <article
              key={item.id}
              className="mmi-premium-card"
            >
              <div className="mmi-premium-image-wrap">
                <img
                  src={item.image}
                  alt={item.title}
                  className="mmi-premium-image"
                  loading="lazy"
                />

                <div
                  className="mmi-premium-image-overlay"
                  aria-hidden="true"
                />

                <span className="mmi-premium-number">
                  {item.number}
                </span>

                <span className="mmi-premium-image-label">
                  {item.imageLabel}
                </span>

                <div className="mmi-premium-icon" aria-hidden="true">
                  {item.icon}
                </div>
              </div>

              <div className="mmi-premium-card-content">
                <span className="mmi-premium-card-category">
                  {item.category}
                </span>

                <h3 className="mmi-premium-card-title">
                  {item.title}
                </h3>

                <p className="mmi-premium-card-description">
                  {item.description}
                </p>

                <div className="mmi-premium-card-footer">
                  <a
                    href="#about"
                    className="mmi-premium-card-link"
                    aria-label={`Learn more about ${item.title}`}
                  >
                    Learn More
                    <FaArrowRight />
                  </a>

                  <span
                    className="mmi-premium-card-star"
                    aria-label="Excellence and quality"
                  >
                    <FaStar />
                    Excellence
                  </span>
                </div>
              </div>
            </article>
          ))}
        </div>

        {/* ===============================================
            BOTTOM BRAND STATEMENT
        =============================================== */}

        <div className="mmi-premium-bottom">
          <div className="mmi-premium-bottom-copy">
            <div className="mmi-premium-bottom-icon">
              <FaGraduationCap />
            </div>

            <div>
              <strong className="mmi-premium-bottom-title">
                Every Child Deserves the Opportunity to Excel
              </strong>

              <span className="mmi-premium-bottom-description">
                Building knowledge, nurturing character, and inspiring
                the leaders of tomorrow.
              </span>
            </div>
          </div>

          <a
            href="#about"
            className="mmi-premium-bottom-badge"
          >
            Discover Our Mission
            <FaArrowRight />
          </a>
        </div>
      </section>
    </>
  );
};

export default Card;
